import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  LayoutDashboard,
  BookOpen,
  GraduationCap,
  Download,
  Megaphone,
  Send,
  Sparkles,
  Settings,
  ArrowRight,
  X,
  CornerDownLeft,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATIC_ITEMS = [
  { title: "Dashboard", subtitle: "Operational homepage", to: "/", icon: LayoutDashboard, group: "Navigate" },
  { title: "Knowledge Base", subtitle: "Search all articles", to: "/knowledge-base", icon: BookOpen, group: "Navigate" },
  { title: "Academy", subtitle: "Trainings & courses", to: "/academy", icon: GraduationCap, group: "Navigate" },
  { title: "Downloads", subtitle: "Files & assets", to: "/downloads", icon: Download, group: "Navigate" },
  { title: "Announcements", subtitle: "Latest updates", to: "/announcements", icon: Megaphone, group: "Navigate" },
  { title: "Telegram Channels", subtitle: "Community channels", to: "/telegram", icon: Send, group: "Navigate" },
  { title: "EM AI", subtitle: "Coming soon", to: "/ai-assistant", icon: Sparkles, group: "Navigate" },
  { title: "Settings", subtitle: "Your profile", to: "/settings", icon: Settings, group: "Navigate" },
];

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(0);
  const [articles, setArticles] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch content once, filter client-side (fast, responsive)
  useEffect(() => {
    if (!open) return;
    setQ("");
    setSelected(0);
    Promise.all([
      api.get("/kb/articles").then((r) => r.data).catch(() => []),
      api.get("/academy/trainings").then((r) => r.data).catch(() => []),
    ]).then(([a, t]) => {
      setArticles(a);
      setTrainings(t);
    });
  }, [open]);

  const items = useMemo(() => {
    const staticFiltered = user?.role === "admin"
      ? [
          ...STATIC_ITEMS,
          { title: "Admin · Knowledge", subtitle: "Manage articles", to: "/admin/knowledge", icon: BookOpen, group: "Admin" },
          { title: "Admin · Academy", subtitle: "Manage trainings", to: "/admin/academy", icon: GraduationCap, group: "Admin" },
          { title: "Admin · Downloads", subtitle: "Manage files", to: "/admin/downloads", icon: Download, group: "Admin" },
          { title: "Admin · Announcements", subtitle: "Manage announcements", to: "/admin/announcements", icon: Megaphone, group: "Admin" },
          { title: "Admin · Telegram", subtitle: "Manage Telegram channels", to: "/admin/telegram", icon: Send, group: "Admin" },
          { title: "Admin · Users", subtitle: "Manage users", to: "/admin/users", icon: Settings, group: "Admin" },
        ]
      : STATIC_ITEMS;

    const kb = articles.map((a) => ({
      title: a.title,
      subtitle: `${a.category}${a.tags?.length ? " · " + a.tags.slice(0, 2).join(", ") : ""}`,
      to: `/knowledge-base/${a.id}`,
      icon: BookOpen,
      group: "Knowledge Base",
    }));
    const tr = trainings.map((t) => ({
      title: t.title,
      subtitle: `${t.difficulty} · ${t.duration_min}m`,
      to: "/academy",
      icon: GraduationCap,
      group: "Academy",
    }));
    const all = [...staticFiltered, ...kb, ...tr];
    if (!q) return all.slice(0, 30);
    const s = q.toLowerCase();
    return all
      .filter(
        (i) => i.title.toLowerCase().includes(s) || (i.subtitle || "").toLowerCase().includes(s)
      )
      .slice(0, 30);
  }, [q, articles, trainings, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") return onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(0, s - 1));
      } else if (e.key === "Enter") {
        const item = items[selected];
        if (item) {
          navigate(item.to);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, selected, onClose, navigate]);

  useEffect(() => setSelected(0), [q]);

  if (!open) return null;

  // Group items for display
  const grouped = items.reduce((acc, item, idx) => {
    (acc[item.group] = acc[item.group] || []).push({ ...item, idx });
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] p-4 bg-ink-0/70 backdrop-blur-md animate-fade-in"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl em-card overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
          <Search className="w-4 h-4 text-zinc-500" strokeWidth={1.75} />
          <input
            data-testid="command-palette-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search EM Operating System..."
            className="flex-1 bg-transparent outline-none text-white placeholder:text-zinc-600 text-base"
          />
          <kbd className="text-[10px] uppercase tracking-widest text-zinc-500 border border-white/10 rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>

        <div className="max-h-[420px] overflow-y-auto py-2">
          {items.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm">
              No results for &quot;{q}&quot;
            </div>
          ) : (
            Object.entries(grouped).map(([group, list]) => (
              <div key={group} className="pb-2">
                <div className="px-5 py-2 text-[10px] uppercase tracking-[0.24em] text-zinc-600 font-semibold">
                  {group}
                </div>
                {list.map((item) => (
                  <button
                    key={`${group}-${item.idx}`}
                    onClick={() => {
                      navigate(item.to);
                      onClose();
                    }}
                    onMouseEnter={() => setSelected(item.idx)}
                    className={`w-full text-left flex items-center gap-3 px-5 py-2.5 group ${
                      selected === item.idx ? "bg-white/5" : "hover:bg-white/[0.03]"
                    }`}
                    style={{ transitionProperty: "background-color", transitionDuration: "120ms" }}
                  >
                    <item.icon
                      className={`w-4 h-4 shrink-0 ${selected === item.idx ? "text-gold" : "text-zinc-500"}`}
                      strokeWidth={1.75}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{item.title}</div>
                      {item.subtitle && (
                        <div className="text-xs text-zinc-500 truncate">{item.subtitle}</div>
                      )}
                    </div>
                    {selected === item.idx && (
                      <CornerDownLeft className="w-3.5 h-3.5 text-gold shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 text-[11px] text-zinc-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <kbd className="border border-white/10 rounded px-1.5 py-0.5">↑</kbd>
              <kbd className="border border-white/10 rounded px-1.5 py-0.5">↓</kbd>
              <span>navigate</span>
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="border border-white/10 rounded px-1.5 py-0.5">↵</kbd>
              <span>open</span>
            </span>
          </div>
          <span>
            <span className="text-gold">EM</span> Operating System
          </span>
        </div>
      </div>
    </div>
  );
}
